import {
  AttentionBlock,
  BlockTitle,
  EmptyState,
  FilterSegments,
  ListRow,
  ListRows,
  PageTitle,
  StatusTag,
  cn,
  titleCase,
} from '@westhetics/ui'
import { useNavigate } from 'react-router'
import { LoadError } from '@/app/load-error'
import { ReadingSkeleton, noteText } from '@/app/shell'
import { cityLine, isoToBrDate } from '@/lib/format'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { useHome } from './api'
import {
  CLINICS_NOTE,
  EMPTY,
  FILTER_KINDS,
  FILTER_LABELS,
  FILTERS,
  SIGNATURES_NOTE,
  countsLine,
  otherEmailTitle,
  timelineOf,
  type Filter,
} from './labels'

/**
 * O início (docs/areas/portal-do-paciente.md, "Início"; RN-POR-04): "Olá, Amanda.", a linha de
 * contagem, os segmentos com número e a linha do tempo da mais recente para a mais antiga; no fim,
 * as clínicas em que ela está e como corrigir um dado. Arquétipo: lista de leitura, no celular.
 */
export function HomePage() {
  const home = useHome()
  const navigate = useNavigate()
  const { filter, setFilter } = useStoredFilter<Filter>('portal-home', FILTERS, 'all')

  if (home.isPending) return <ReadingSkeleton label="Carregando seus atendimentos" />
  if (home.isError) {
    return (
      <LoadError
        title="Não conseguimos carregar seus atendimentos"
        error={home.error}
        onRetry={() => void home.refetch()}
      />
    )
  }

  const data = home.data
  const items = timelineOf(data).filter(
    (item) => filter === 'all' || item.kind === FILTER_KINDS[filter],
  )
  const empty = EMPTY[filter]

  return (
    <div className="flex flex-col gap-6">
      <PageTitle
        eyebrow="Portal do paciente"
        title={data.patient.firstName ? `Olá, ${titleCase(data.patient.firstName)}.` : 'Olá.'}
        meta={countsLine(data)}
      />

      {data.otherEmailRecords > 0 ? (
        <AttentionBlock variant="lilac" title={otherEmailTitle(data.otherEmailRecords)}>
          Para vê-los, saia e entre de novo com o seu documento, escolhendo o outro e-mail.
        </AttentionBlock>
      ) : null}

      <FilterSegments<Filter>
        label="Mostrar"
        value={filter}
        onChange={setFilter}
        options={FILTERS.map((value) => ({
          value,
          label: FILTER_LABELS[value],
          count: value === 'all' ? data.counts.all : data.counts[value],
        }))}
      />

      {items.length === 0 ? (
        <EmptyState title={empty.title} description={empty.description} />
      ) : (
        <ListRows aria-label="Linha do tempo">
          {items.map((item) => (
            <ListRow
              key={item.key}
              size="name"
              eyebrow={item.eyebrow}
              title={item.title}
              meta={item.meta}
              aria-label={`${item.title} · ${item.eyebrow}`}
              trailing={
                item.tag ? <StatusTag level={item.tag.level}>{item.tag.label}</StatusTag> : null
              }
              onClick={() => navigate(item.href)}
            />
          ))}
        </ListRows>
      )}
      <p className={noteText}>{SIGNATURES_NOTE}</p>

      <section aria-label="Suas clínicas" className="flex flex-col gap-3">
        <BlockTitle title="Suas clínicas" note={CLINICS_NOTE} />
        {data.clinics.length === 0 ? (
          <p className={cn(noteText)}>Nenhuma clínica tem este e-mail no cadastro.</p>
        ) : (
          <ListRows>
            {data.clinics.map((clinic, index) => (
              <ListRow
                key={`${clinic.id}:${index}`}
                title={clinic.name}
                meta={[cityLine(clinic), `paciente desde ${isoToBrDate(clinic.patientSince)}`]
                  .filter(Boolean)
                  .join(' · ')}
              />
            ))}
          </ListRows>
        )}
      </section>
    </div>
  )
}
